import React, { useState, useEffect } from "react";
import { Tabs } from "antd";
import { TeamOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import SkeyenoteUsersTable from "./SkeyenoteUsersTable";
import SkeyenoteRolesTable from "./SkeyenoteRolesTable";
import {
  AddUserModal,
  ChangeEmailModal,
  ResetPasswordModal,
  ManageRolesModal,
  AddRoleModal,
} from "./SkeyenoteUsersDialogs";
import {
  fetchUsers,
  addUser,
  deleteUser,
  fetchRoles,
  addRole,
  deleteRole,
  changeEmail,
  resetPassword,
  assignRole,
  removeRole,
} from "./api/SkeyenoteUsersAPI";
import PageLayout from "../../../components/PageLayout";
import SearchToolbar from "../../../components/SearchToolbar";
import usePageTitle from "../../../services/usePageTitle";
import usePageState from "../../../hooks/usePageState";
import useIsRoleUser from "../../../hooks/useIsRoleUser";
import { extractMsg } from "../../../utils/errorUtils";
import skeyenoteUsersHelp from "../../../content/help/skeyenote-users.json";

export default function SkeyenoteUsers({ pageTitle = "Skeyenote Users" }) {
  usePageTitle("SkeyeNote Users - SkeyeGate™");

  const {
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    onPageChange,
  } = usePageState({
    fetchFn: fetchUsers,
    filterFields: [
      "username",
      (u) => (Array.isArray(u.roles) ? u.roles.join(" ") : ""),
    ],
    defaultPageSize: 25,
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const isRoleUser = useIsRoleUser();

  // ---- New modals ----
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [rolesSearchText, setRolesSearchText] = useState("");
  const handleAddUser = async (values) => {
    try {
      await addUser({
        username: values.username,
        password: values.password,
        roles: values.roles,
      });
      messageApi.success(`User "${values.username}" has been added`);
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteUser = async (username) => {
    try {
      await deleteUser(username);
      messageApi.success(`User "${username}" has been deleted`);
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to delete user"),
        key: "delete-user-error",
      });
    }
  };

  const handleChangeEmail = async (username, email) => {
    try {
      await changeEmail(username, email);
      messageApi.success(`Email updated for "${username}"`);
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleResetPassword = async (username, password) => {
    try {
      await resetPassword(username, password);
      messageApi.success(`Password reset for "${username}"`);
    } catch (error) {
      throw error;
    }
  };

  const handleAssignRole = async (username, roleName) => {
    try {
      await assignRole(username, roleName);
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleRemoveRole = async (username, roleName) => {
    try {
      await removeRole(username, roleName);
      loadData();
    } catch (error) {
      throw error;
    }
  };

  // ---- Roles state ----
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleOptionsLoading, setRoleOptionsLoading] = useState(false);
  const filteredRoles = rolesSearchText
    ? roles.filter((r) =>
        r.name.toLowerCase().includes(rolesSearchText.toLowerCase()),
      )
    : roles;

  const loadRoleOptions = async () => {
    setRoleOptionsLoading(true);
    try {
      const roles = await fetchRoles();
      setRoleOptions(
        Array.isArray(roles) ? roles.map((r) => ({ label: r, value: r })) : [],
      );
    } catch {
      setRoleOptions([]);
    } finally {
      setRoleOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (addModalOpen || emailModalOpen || passwordModalOpen || rolesModalOpen) {
      loadRoleOptions();
    }
  }, [addModalOpen, emailModalOpen, passwordModalOpen, rolesModalOpen]);

  const loadRoles = async () => {
    setRolesLoading(true);
    try {
      const data = await fetchRoles();
      setRoles(Array.isArray(data) ? data.map((r) => ({ name: r })) : []);
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to load roles"),
        key: "load-roles-error",
      });
    } finally {
      setRolesLoading(false);
    }
  };

  const handleAddRole = async (roleName) => {
    try {
      await addRole(roleName);
      messageApi.success(`Role "${roleName}" has been added`);
      loadRoles();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteRole = async (roleName) => {
    try {
      await deleteRole(roleName);
      messageApi.success(`Role "${roleName}" has been deleted`);
      loadRoles();
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to delete role"),
        key: "delete-role-error",
      });
    }
  };

  const tabItems = [
    {
      key: "users",
      label: (
        <span>
          <TeamOutlined /> Users
        </span>
      ),
      children: (
        <>
          <SearchToolbar
            search={searchText}
            onSearchChange={handleSearch}
            onClearFilter={clearFilter}
            addButtonLabel="Add User"
            onAdd={() => setAddModalOpen(true)}
            showAdd={!isRoleUser}
          />
          <SkeyenoteUsersTable
            data={filteredData}
            loading={loading}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onEditEmail={(user) => {
              setSelectedUser(user);
              setEmailModalOpen(true);
            }}
            onEditRoles={(user) => {
              setSelectedUser(user);
              setRolesModalOpen(true);
            }}
            onResetPassword={(user) => {
              setSelectedUser(user);
              setPasswordModalOpen(true);
            }}
            onDeleteUser={handleDeleteUser}
            isRoleUser={isRoleUser}
          />
        </>
      ),
    },
    {
      key: "roles",
      label: (
        <span>
          <SafetyCertificateOutlined /> Roles
        </span>
      ),
      children: (
        <>
          <SearchToolbar
            search={rolesSearchText}
            onSearchChange={setRolesSearchText}
            onClearFilter={() => setRolesSearchText("")}
            addButtonLabel="Add Role"
            onAdd={() => setAddRoleModalOpen(true)}
            showAdd={!isRoleUser}
          />
          <SkeyenoteRolesTable
            data={filteredRoles}
            loading={rolesLoading}
            page={1}
            pageSize={100}
            onPageChange={() => {}}
            onDeleteRole={handleDeleteRole}
            isRoleUser={isRoleUser}
          />
        </>
      ),
    },
  ];

  return (
    <div className="skeyenote-users-page">
      {contextHolder}
      <PageLayout
        title={pageTitle}
        logo="/img/majesteye-icon-skeye-note-square-white-background-no-label"
        description="Manager for Skeyenote Users and Roles."
        helpContent={skeyenoteUsersHelp}
      >
        <Tabs
          defaultActiveKey="users"
          items={tabItems}
          onChange={(key) => {
            if (key === "roles") loadRoles();
          }}
        />
      </PageLayout>

      <AddUserModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddUser}
        roleOptions={roleOptions}
        roleOptionsLoading={roleOptionsLoading}
      />
      <ChangeEmailModal
        open={emailModalOpen}
        user={selectedUser}
        onClose={() => setEmailModalOpen(false)}
        onSave={handleChangeEmail}
      />
      <ResetPasswordModal
        open={passwordModalOpen}
        user={selectedUser}
        onClose={() => setPasswordModalOpen(false)}
        onSave={handleResetPassword}
      />
      <ManageRolesModal
        open={rolesModalOpen}
        user={selectedUser}
        onClose={() => setRolesModalOpen(false)}
        onAssignRole={handleAssignRole}
        onRemoveRole={handleRemoveRole}
        roleOptions={roleOptions}
        roleOptionsLoading={roleOptionsLoading}
      />
      <AddRoleModal
        open={addRoleModalOpen}
        onClose={() => setAddRoleModalOpen(false)}
        onSave={handleAddRole}
      />
    </div>
  );
}
